import type { StreamingChatId, StructuredSearchResult } from "@storytell/streaming";
import { TbCheck, TbChevronDown, TbChevronRight, TbClock, TbDatabase, TbSearch, TbTable } from "solid-icons/tb";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { LoadingDots, ScrollableFadeContainer } from "../ui";
import { collapsibleOpenHint } from "../util/collapsibleOpenHint";

interface StructuredResultsRendererProps {
  structuredResults: StructuredSearchResult;
  chatId?: string;
}

interface TableRendererProps {
  headers?: string[];
  rows?: string[][];
  chatId?: string;
}

// Renders **text** spans as bold; everything else passes through as text.
const renderCellContent = (content: string) => {
  const parts = content.split(/\*\*(.+?)\*\*/);
  return (
    <>
      <For each={parts}>
        {(part, index) => (
          <Show when={index() % 2 === 1} fallback={part}>
            <strong class="font-semibold text-gray-900 dark:text-gray-100">{part}</strong>
          </Show>
        )}
      </For>
    </>
  );
};

const formatDuration = (duration?: number | { seconds?: number; nanos?: number }) => {
  if (!duration) return null;

  let seconds: number;
  if (typeof duration === "object" && "seconds" in duration) {
    seconds = duration.seconds || 0;
  } else {
    seconds = Number(duration);
  }

  if (Number.isNaN(seconds)) return null;
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  return `${seconds.toFixed(1)}s`;
};

const TableRenderer = (props: TableRendererProps) => {
  const tableData = createMemo(() => ({
    headers: props.headers || [],
    rows: props.rows || [],
  }));

  const rt = useChatRuntime();
  const [tableRef, setTableRef] = createSignal<HTMLTableElement>();

  return (
    <Show when={tableData().headers.length > 0}>
      <ScrollableFadeContainer maxHeight="300px">
        <div class="overflow-x-auto rounded-md border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
          <table ref={(el) => setTableRef(el)} class="min-w-full divide-y divide-gray-200/50 dark:divide-gray-700/50">
            <thead class="bg-gray-100/50 dark:bg-gray-800/30">
              <tr>
                <For each={tableData().headers}>
                  {(header) => (
                    <th
                      scope="col"
                      class="px-3 py-2 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase dark:text-gray-300"
                    >
                      {renderCellContent(header)}
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200/30 dark:divide-gray-700/30">
              <For each={tableData().rows}>
                {(row) => (
                  <tr class="group transition-colors hover:bg-gray-100/50 dark:hover:bg-gray-800/30">
                    <For each={row}>
                      {(cell) => (
                        <td class="px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                          <span class="block">
                            {cell ? renderCellContent(cell) : <span class="text-gray-400 dark:text-gray-600">—</span>}
                          </span>
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={tableRef()}>
            {(tableEl) => rt.slots?.tableActions?.({ tableElement: tableEl(), chatId: props.chatId })}
          </Show>
        </div>
      </ScrollableFadeContainer>
    </Show>
  );
};

/**
 * Component to render a beautiful completion message for structured data queries
 */
const CompletionMessage = (props: {
  duration?: number | { seconds?: number; nanos?: number };
  assetCount?: number;
}) => {
  const rt = useChatRuntime();
  const [isVisible, setIsVisible] = createSignal(false);

  onMount(() => {
    setTimeout(() => setIsVisible(true), 100);
  });

  return (
    <div
      class={twMerge(
        "flex items-center gap-3 rounded-lg border p-3",
        "bg-linear-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20",
        "border-green-200/50 dark:border-green-700/50",
        "transform transition-all duration-500",
        isVisible() ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
    >
      <div class="flex size-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-800/30">
        <TbCheck class="size-4 text-green-600 dark:text-green-400" />
      </div>
      <div class="flex-1">
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
          {rt.t("common.successfully_queried_structured_data")}
        </p>
        <div class="mt-1 flex items-center gap-3">
          <Show when={props.assetCount}>
            <span class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
              <TbDatabase class="size-3" />
              {props.assetCount}{" "}
              {props.assetCount === 1 ? rt.t("structured_data_source") : rt.t("structured_data_sources")}
            </span>
          </Show>
          <Show when={props.duration}>
            <span class="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-800/30 dark:text-green-300">
              <TbClock class="size-3" />
              {formatDuration(props.duration)}
            </span>
          </Show>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders structured SQL query results in a compact expandable format
 */
export const StructuredResultsRenderer = (props: StructuredResultsRendererProps) => {
  const rt = useChatRuntime();
  const [isExpanded, setIsExpanded] = createSignal(false);

  const resultCount = () => props.structuredResults.rowCount || props.structuredResults.rows?.length || 0;

  const normalizedRows = () => props.structuredResults.rows || [];

  const isTruncated = createMemo(() => {
    const totalRows = props.structuredResults.rowCount || 0;
    const displayedRows = props.structuredResults.rows?.length || 0;
    return totalRows > displayedRows;
  });

  const rowCountText = createMemo(() => {
    const totalRows = resultCount();
    const displayedRows = props.structuredResults.rows?.length || 0;

    if (isTruncated()) {
      return `${displayedRows} of ${totalRows}`;
    }
    return `${totalRows}`;
  });

  const isCompletionMessage = createMemo(() => {
    return (
      props.structuredResults.isCompletion ||
      (props.structuredResults.assetName === "Completion Summary" &&
        !props.structuredResults.rows?.length &&
        !props.structuredResults.markdownTable)
    );
  });

  const completionData = createMemo(() => ({
    duration: props.structuredResults.duration,
    assetCount: props.structuredResults.assetCount,
  }));

  return (
    <Show
      when={!isCompletionMessage()}
      fallback={<CompletionMessage duration={completionData().duration} assetCount={completionData().assetCount} />}
    >
      <div class="border-b border-gray-200 last:border-b-0 dark:border-slate-700">
        <div class="group/structured-results flex w-full items-center gap-2 px-3 py-2.5 hover:bg-canvas-hover">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded())}
            class="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
          >
            <TbTable class="size-4 shrink-0 text-green-600 dark:text-green-400" />
            <span class="flex-1 truncate text-left text-sm text-primary-text">
              {props.structuredResults.assetName || "Structured Data"}
            </span>
            <Show when={isExpanded()}>
              <span class="shrink-0 text-xs text-muted-text">{rowCountText()} rows</span>
            </Show>
            <Show when={isExpanded() && props.structuredResults.duration}>
              <span class="shrink-0 text-xs text-muted-text">{formatDuration(props.structuredResults.duration)}</span>
            </Show>
            <TbChevronDown
              size={14}
              class="shrink-0 text-muted-text transition-transform duration-100"
              classList={{ "rotate-180": isExpanded() }}
            />
          </button>
          <Show when={!isExpanded()}>
            <span class={collapsibleOpenHint.structuredResults} aria-hidden>
              {rt.t("common.open")}
            </span>
          </Show>
        </div>

        <Show when={isExpanded()}>
          <div class="px-3 pt-1 pb-3">
            <Show when={props.structuredResults.queryReason}>
              <div class="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900/30">
                <p class="text-sm/relaxed text-gray-700 dark:text-gray-300">{props.structuredResults.queryReason}</p>
              </div>
            </Show>

            <Show
              when={resultCount() > 0}
              fallback={
                <p class="py-2 text-sm text-gray-500 dark:text-gray-400">No matching records found for this query.</p>
              }
            >
              <Show when={props.structuredResults.headers?.length || normalizedRows().length}>
                <TableRenderer
                  headers={props.structuredResults.headers || []}
                  rows={normalizedRows()}
                  chatId={props.chatId}
                />

                <Show when={isTruncated()}>
                  <div class="flex items-center justify-center pt-3 text-xs text-gray-500 dark:text-gray-400">
                    Showing first {normalizedRows().length} of {resultCount()} total rows
                  </div>
                </Show>
              </Show>

              <Show when={props.structuredResults.markdownTable && !props.structuredResults.headers?.length}>
                <TableRenderer headers={[]} rows={[]} chatId={props.chatId} />
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    </Show>
  );
};

interface StructuredSearchItemProps {
  query: string;
  structuredResults: StructuredSearchResult;
  chatId: StreamingChatId;
  itemKey: string;
  isProcessing?: boolean;
}

export const StructuredSearchItemRenderer = (props: StructuredSearchItemProps) => {
  const rt = useChatRuntime();
  // Persistent expand state when the host provides it (bluesky → localStorage);
  // otherwise a local signal so the row still collapses (chief).
  const persist = rt.ui?.usePersistentExpanded;
  const [isExpanded, setIsExpanded] = persist
    ? persist(props.chatId, `structured-search:${props.itemKey}`, false)
    : (() => {
        const [exp, setExp] = createSignal(false);
        const set = (v: boolean, _isManual?: boolean) => setExp(v);
        return [exp, set, () => false] as const;
      })();

  const isCompletionMessage = createMemo(() => {
    return (
      props.structuredResults.isCompletion ||
      (props.structuredResults.assetName === "Completion Summary" &&
        !props.structuredResults.rows?.length &&
        !props.structuredResults.markdownTable)
    );
  });

  const rowCount = createMemo(() => {
    return props.structuredResults.rowCount || props.structuredResults.rows?.length || 0;
  });

  const headerText = createMemo(() => {
    if (props.query) return `"${props.query}"`;
    return props.structuredResults.assetName || "Structured Data";
  });

  if (isCompletionMessage()) {
    return null;
  }

  return (
    <div class="border-b border-gray-200 last:border-b-0 dark:border-slate-700">
      <div class="group/structured-search-item flex w-full items-center gap-2 px-3 py-2.5 hover:bg-canvas-hover">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded(), true)}
          class="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
        >
          <TbChevronRight
            size={14}
            class="shrink-0 text-muted-text transition-transform duration-100"
            classList={{ "rotate-90": isExpanded() }}
          />
          <TbSearch class="size-4 shrink-0 text-muted-text" />
          <span class="flex-1 truncate text-left text-sm text-primary-text">{headerText()}</span>
          <Show when={props.isProcessing}>
            <LoadingDots />
          </Show>
          <Show when={isExpanded() && !props.isProcessing && rowCount() > 0}>
            <span class="shrink-0 text-xs text-muted-text">{rowCount()} rows</span>
          </Show>
          <Show when={isExpanded() && !props.isProcessing && props.structuredResults.duration}>
            <span class="shrink-0 text-xs text-muted-text">{formatDuration(props.structuredResults.duration)}</span>
          </Show>
        </button>
        <Show when={!isExpanded()}>
          <span class={collapsibleOpenHint.structuredSearchItem} aria-hidden>
            {rt.t("common.open")}
          </span>
        </Show>
      </div>

      <Show when={isExpanded()}>
        <div class="px-3 pb-3">
          <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
            <StructuredResultsRenderer structuredResults={props.structuredResults} />
          </div>
        </div>
      </Show>
    </div>
  );
};
